import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSPrompt, setShowIOSPrompt] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);
    
    if (standalone) return;

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent);
    
    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa_dismissed');
    const iosDismissed = localStorage.getItem('ios_pwa_dismissed');
    
    if (dismissed) {
      const dismissedTime = new Date(dismissed).getTime();
      const now = new Date().getTime();
      // Don't show for 7 days after dismiss
      if (now - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        return;
      }
    }
    
    if (ios && !iosDismissed) {
      // Show iOS prompt after 3 seconds
      setTimeout(() => setShowIOSPrompt(true), 3000);
      return;
    }

    // Listen for beforeinstallprompt (Android/Desktop)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 5 seconds
      setTimeout(() => setShowPrompt(true), 5000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('PWA installed');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setShowIOSPrompt(false);
    localStorage.setItem('pwa_dismissed', new Date().toISOString());
  };

  const handleIOSDismiss = () => {
    setShowIOSPrompt(false);
    localStorage.setItem('ios_pwa_dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone) return null;

  // iOS specific instructions
  if (showIOSPrompt) {
    return (
      <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 z-50 animate-in slide-in-from-bottom duration-300">
        <button 
          onClick={handleIOSDismiss}
          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
        
        <div className="flex items-start gap-3 pr-6">
          <div className="p-2 bg-emerald-100 rounded-xl flex-shrink-0">
            <Smartphone className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900 mb-1">Installer LEMO</h3>
            <p className="text-sm text-gray-600 mb-3">
              Ajoutez LEMO à votre écran d'accueil
            </p>
            <div className="text-xs text-gray-500 space-y-1">
              <p>1. Appuyez sur <span className="inline-flex items-center px-1.5 py-0.5 bg-gray-100 rounded">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a.75.75 0 01.75.75v5.59l1.95-2.1a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0L6.2 7.26a.75.75 0 111.1-1.02l1.95 2.1V2.75A.75.75 0 0110 2z"/>
                  <path d="M5.273 4.5a1.25 1.25 0 00-1.023 1.975l4.25 6.08a1.75 1.75 0 002.5 0l4.25-6.08A1.25 1.25 0 0014.227 4.5H5.273z"/>
                </svg>
                Partager
              </span></p>
              <p>2. Puis "Sur l'écran d'accueil"</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Android/Desktop prompt
  if (!showPrompt || !deferredPrompt) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 z-50 animate-in slide-in-from-bottom duration-300">
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
        aria-label="Fermer"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-start gap-3 pr-6">
        <div className="p-2 bg-emerald-100 rounded-xl flex-shrink-0">
          <Download className="w-6 h-6 text-emerald-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 mb-1">Installer LEMO</h3>
          <p className="text-sm text-gray-600 mb-3">
            Installez l'app pour commander plus rapidement !
          </p>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDismiss}
              className="flex-1"
            >
              Plus tard
            </Button>
            <Button 
              size="sm" 
              onClick={handleInstall}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              Installer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
